import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import MediaServicesSearchClient from "@foundation/media-services-search-client";
import { debounce } from "@foundation/util";
import { usePageData } from "~context/PageContext";
import { getSessionIdCookie } from "~utils/sessionUtils";
import type { SearchQueryTicker, SearchQueryResult } from "~types/types";

const INPUT_DELAY = 300;

type TickerType =
  | "company_public"
  | "Index"
  | "Fund"
  | "Currency"
  | "Commodity"
  | "Bond";

const DEFAULT_TYPES: TickerType[] = [
  "company_public",
  "Index",
  "Fund",
  "Currency",
  "Commodity",
  "Bond",
];

interface TickerSearchOptions {
  readonly searchQuery: string;
  readonly excludedTickers: string[];
  readonly types?: TickerType[];
}

interface UseTickerSearchReturn {
  readonly results: SearchQueryTicker[];
  readonly isLoading: boolean;
}

export type { TickerType, TickerSearchOptions, UseTickerSearchReturn };

export function useTickerSearch({
  searchQuery,
  excludedTickers,
  types = DEFAULT_TYPES,
}: TickerSearchOptions): UseTickerSearchReturn {
  const { countryCode, searchClientURL } = usePageData();

  const [results, setResults] = useState<SearchQueryTicker[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchClient = useMemo(
    () => new MediaServicesSearchClient({ baseUrl: searchClientURL }),
    [searchClientURL]
  );

  const excludedSet = useMemo(
    () => new Set(excludedTickers),
    [excludedTickers]
  );

  const fetchResults = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setResults([]);
        setIsLoading(false);

        return;
      }

      setIsLoading(true);

      try {
        const { results: searchResults }: SearchQueryResult =
          await searchClient.lookup({
            query,
            includeBlocklisted: false,
            source: getSessionIdCookie(),
            size: 100,
            country: countryCode,
            types,
            boostParents: true,
          });

        const filtered = (searchResults ?? []).filter(
          (item) => !excludedSet.has(item.resource_id)
        );

        setResults(filtered);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [searchClient, countryCode, types, excludedSet]
  );

  const debouncedFetch = useMemo(
    () => debounce(fetchResults, INPUT_DELAY),
    [fetchResults]
  );

  const debouncedFetchRef = useRef(debouncedFetch);

  debouncedFetchRef.current = debouncedFetch;

  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);
    debouncedFetchRef.current(searchQuery);
  }, [searchQuery]);

  return { results, isLoading };
}
